/**
 * The forgot-password send, with its resend cooldown (DOP-275).
 *
 * Two places ask for a reset mail — the standalone `/forgot-password` page and
 * the dialog on Settings — and both need the same three things: post the
 * address, remember which address it was, and refuse a resend for 45 seconds.
 * Keeping that here is what stops the second copy drifting from the first.
 *
 * The cooldown is a courtesy, not a control: the real budget is the server's
 * (three sends per 15 minutes per address). It exists so the user does not
 * spend their server-side budget by clicking four times in five seconds and
 * then find the resend that mattered silently dropped.
 */

import { useCallback, useEffect, useState } from "react";
import { authService } from "../services/authApi";

export const RESEND_COOLDOWN_SECONDS = 45;

export interface ForgotPasswordSend {
	/** The address a send succeeded for, or null before the first one. */
	sentTo: string | null;
	/** A send is in flight. */
	sending: boolean;
	/** The last failure, cleared when a send is attempted. */
	error: string;
	/** Seconds left before a resend is allowed; 0 means it is allowed now. */
	cooldownSeconds: number;
	send: (address: string) => Promise<void>;
	/** Re-send to `sentTo`. A no-op while the cooldown runs or a send is out. */
	resend: () => Promise<void>;
	/** Forget the send — back to the state before the first submit. */
	reset: () => void;
}

export const useForgotPasswordSend = (): ForgotPasswordSend => {
	const [sentTo, setSentTo] = useState<string | null>(null);
	const [error, setError] = useState("");
	const [sending, setSending] = useState(false);
	const [cooldownSeconds, setCooldownSeconds] = useState(0);

	// One timeout per remaining second rather than a single interval: the
	// effect re-runs on each decrement, so there is never a live timer left
	// behind when the caller unmounts mid-countdown.
	useEffect(() => {
		if (cooldownSeconds <= 0) return;
		const timer = setTimeout(() => setCooldownSeconds((s) => s - 1), 1000);
		return () => clearTimeout(timer);
	}, [cooldownSeconds]);

	const send = useCallback(async (address: string) => {
		setError("");
		setSending(true);
		try {
			await authService.forgotPassword(address);
			setSentTo(address);
			setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
		} catch (err: any) {
			setError(err.message || "Failed to send reset email");
		} finally {
			setSending(false);
		}
	}, []);

	const resend = useCallback(async () => {
		if (sentTo === null || cooldownSeconds > 0 || sending) return;
		await send(sentTo);
	}, [cooldownSeconds, send, sending, sentTo]);

	const reset = useCallback(() => {
		setSentTo(null);
		setError("");
		setCooldownSeconds(0);
	}, []);

	return { sentTo, sending, error, cooldownSeconds, send, resend, reset };
};

export default useForgotPasswordSend;
